/**
 * PipGame - Premium Voice-Controlled Game
 * 
 * FIXES:
 * - Stricter word matching (minimum length, exact word boundaries)
 * - Only show "Are you ready?" on first level
 * - Better false positive filtering
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { useSpeechSynthesis } from '../../hooks/useSpeechSynthesis';
import { useAudioEffects } from '../../hooks/useAudioEffects';

// Sub-components
import TitleScreen from './screens/TitleScreen';
import GameScreen from './screens/GameScreen';
import SettingsModal from './screens/SettingsModal';
import CompleteScreen from './screens/CompleteScreen';

import './PipGame.css';

// =============================================================================
// LEVEL DEFINITIONS
// =============================================================================
const LEVELS = [
  {
    id: 1,
    word: 'open',
    acceptedWords: ['open', 'opened'],
    instruction: 'Help Pip open the fridge!',
    pipSays: 'I want to see what\'s inside!',
    action: 'openFridge',
    sceneSetup: { pipPosition: 'center', fridgeOpen: false },
  },
  {
    id: 2,
    word: 'juice',
    acceptedWords: ['juice'],
    instruction: 'Pip sees juice! Say JUICE!',
    pipSays: 'Ooh, I see something yummy!',
    action: 'getJuice',
    sceneSetup: { pipPosition: 'fridge', fridgeOpen: true, highlightItem: 'juice' },
  },
  {
    id: 3,
    word: 'drink',
    acceptedWords: ['drink', 'drinking'],
    instruction: 'Pip wants to drink!',
    pipSays: 'I\'m so thirsty!',
    action: 'drink',
    sceneSetup: { pipPosition: 'center', pipHolding: 'juice' },
  },
  {
    id: 4,
    word: 'more',
    acceptedWords: ['more'],
    instruction: 'Pip wants more!',
    pipSays: 'That was so good!',
    action: 'askMore',
    sceneSetup: { pipPosition: 'center' },
  },
  {
    id: 5,
    word: 'apple',
    acceptedWords: ['apple', 'apples'],
    instruction: 'Pip sees an apple!',
    pipSays: 'I\'m still hungry!',
    action: 'getApple',
    sceneSetup: { pipPosition: 'fridge', fridgeOpen: true, highlightItem: 'apple' },
  },
  {
    id: 6,
    word: 'eat',
    acceptedWords: ['eat', 'eating'],
    instruction: 'Time to eat!',
    pipSays: 'Yum yum time!',
    action: 'eat',
    sceneSetup: { pipPosition: 'center', pipHolding: 'apple' },
  },
  {
    id: 7,
    word: 'yum',
    acceptedWords: ['yum', 'yummy'],
    instruction: 'Was that yummy?',
    pipSays: 'Mmmmm!',
    action: 'sayYum',
    sceneSetup: { pipPosition: 'center' },
  },
  {
    id: 8,
    word: 'close',
    acceptedWords: ['close', 'closed'],
    instruction: 'Close the fridge!',
    pipSays: 'Time to close up!',
    action: 'closeFridge',
    sceneSetup: { pipPosition: 'fridge', fridgeOpen: true },
  },
  {
    id: 9,
    word: 'happy',
    acceptedWords: ['happy'],
    instruction: 'Is Pip happy?',
    pipSays: 'I had such a great snack!',
    action: 'beHappy',
    sceneSetup: { pipPosition: 'center', fridgeOpen: false },
  },
  {
    id: 10,
    word: 'bye',
    acceptedWords: ['bye', 'bye-bye', 'goodbye'],
    instruction: 'Say goodbye to Pip!',
    pipSays: 'Thanks for playing with me!',
    action: 'sayBye',
    sceneSetup: { pipPosition: 'center' },
  },
];

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function PipGame({ onExit }) {
  // =========== STATE ===========
  const [screen, setScreen] = useState('title'); // title, game, complete
  const [currentLevel, setCurrentLevel] = useState(0);
  const [stars, setStars] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [isGameStarted, setIsGameStarted] = useState(false); // Track if game just started
  const [isProcessing, setIsProcessing] = useState(false); // Prevent double triggers
  
  // Settings
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('pip_settings');
      return saved ? JSON.parse(saved) : {
        voiceEnabled: true,
        soundEffects: true,
        speechRate: 0.9,
        autoListen: true,
      };
    } catch {
      return { voiceEnabled: true, soundEffects: true, speechRate: 0.9, autoListen: true };
    }
  });

  // Progress persistence
  const [savedProgress, setSavedProgress] = useState(() => {
    try {
      const saved = localStorage.getItem('pip_progress');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // =========== HOOKS ===========
  const {
    isListening,
    isSupported: micSupported,
    transcript,
    interimTranscript,
    error: micError,
    startListening,
    stopListening,
    onResult,
    clearTranscript,
  } = useSpeechRecognition();

  const {
    isSupported: ttsSupported,
    isSpeaking,
    speak,
    speakAsPip,
    speakPraise,
    cancel: cancelSpeech,
    setRate,
  } = useSpeechSynthesis();

  const {
    playSuccess,
    playClick,
    playWhoosh,
    playPop,
    playFanfare,
    playMunch,
    playGulp,
    playFridge,
  } = useAudioEffects(settings.soundEffects);

  // =========== REFS ===========
  const gameScreenRef = useRef(null);
  const lastMatchTimeRef = useRef(0);

  // =========== EFFECTS ===========
  
  // Save settings when changed
  useEffect(() => {
    localStorage.setItem('pip_settings', JSON.stringify(settings));
    setRate(settings.speechRate);
  }, [settings]);

  // Save progress when it changes
  useEffect(() => {
    if (screen === 'game' && currentLevel > 0) {
      localStorage.setItem('pip_progress', JSON.stringify({
        level: currentLevel,
        stars: stars,
        timestamp: Date.now(),
      }));
    }
  }, [currentLevel, stars, screen]);

  // Handle speech recognition results
  useEffect(() => {
    onResult((text) => {
      if (screen === 'game' && !isProcessing) {
        checkWord(text);
      }
    });
  }, [screen, currentLevel, isProcessing]);

  // =========== GAME LOGIC ===========
  
  const level = LEVELS[currentLevel];

  // STRICTER word matching
  const checkWord = useCallback((text) => {
    if (!level) return;
    if (isProcessing) return;
    
    // Debounce - prevent rapid triggers
    const now = Date.now();
    if (now - lastMatchTimeRef.current < 1500) return;
    
    const normalizedText = text.toLowerCase().trim();
    
    // Minimum length check - filter out noise
    if (normalizedText.length < 2) return;
    
    // STRICT matching: word must appear as a whole word, not part of another
    const matched = level.acceptedWords.some(word => {
      const wordLower = word.toLowerCase();
      // Check for exact word boundaries
      const regex = new RegExp(`\\b${wordLower}\\b`, 'i');
      return regex.test(normalizedText);
    });

    if (matched) {
      lastMatchTimeRef.current = now;
      setIsProcessing(true);
      handleSuccess();
    } else if (normalizedText.length >= 3) {
      // Only show hint for meaningful words
      handleTryAgain(normalizedText);
    }
  }, [level, isProcessing]);

  const handleSuccess = useCallback(() => {
    if (settings.soundEffects) playSuccess();
    
    // Tell GameScreen to perform the action
    if (gameScreenRef.current) {
      gameScreenRef.current.performAction(level.action);
    }
  }, [level, settings.soundEffects, playSuccess]);

  const handleTryAgain = useCallback((heard) => {
    // Don't spam hints
    if (gameScreenRef.current) {
      gameScreenRef.current.showHint(`Try saying "${level.word.toUpperCase()}"`);
    }
  }, [level]);

  const advanceLevel = useCallback(() => {
    const newStars = stars + 1;
    setStars(newStars);
    
    if (settings.soundEffects) playFanfare();
    
    // Clear transcript for next level
    clearTranscript();
    
    // Allow processing again after a delay
    setTimeout(() => {
      setIsProcessing(false);
    }, 500);
    
    if (currentLevel >= LEVELS.length - 1) {
      // Game complete!
      setTimeout(() => {
        setScreen('complete');
        localStorage.removeItem('pip_progress');
      }, 1500);
    } else {
      // Next level - NO "are you ready" - just continue
      setTimeout(() => {
        setCurrentLevel(prev => prev + 1);
        setIsGameStarted(false); // Not first level anymore
      }, 1000);
    }
  }, [currentLevel, stars, settings.soundEffects, playFanfare, clearTranscript]);

  // =========== NAVIGATION ===========

  const handleStartNew = useCallback(() => {
    playClick();
    setCurrentLevel(0);
    setStars(0);
    setIsGameStarted(true); // First level - show ready
    setIsProcessing(false);
    setScreen('game');
    localStorage.removeItem('pip_progress');
  }, [playClick]);

  const handleContinue = useCallback(() => {
    playClick();
    if (savedProgress) {
      setCurrentLevel(savedProgress.level);
      setStars(savedProgress.stars);
    }
    setIsGameStarted(true); // Show ready when continuing
    setIsProcessing(false);
    setScreen('game');
  }, [savedProgress, playClick]);

  const handleReady = useCallback(() => {
    playWhoosh();
    setIsGameStarted(false); // Ready was clicked, no longer first
    
    if (settings.autoListen) {
      startListening();
    }
    
    // Announce the word
    if (settings.voiceEnabled && level) {
      setTimeout(() => {
        speak(`Say ${level.word.toUpperCase()}!`);
      }, 500);
    }
  }, [settings, level, playWhoosh, startListening, speak]);

  const handleHome = useCallback(() => {
    playClick();
    stopListening();
    cancelSpeech();
    setScreen('title');
    setIsGameStarted(false);
    setIsProcessing(false);
  }, [playClick, stopListening, cancelSpeech]);

  const handlePlayAgain = useCallback(() => {
    playClick();
    setCurrentLevel(0);
    setStars(0);
    setIsGameStarted(true);
    setIsProcessing(false);
    setScreen('game');
  }, [playClick]);

  const handleToggleMic = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
      if (settings.voiceEnabled && level) {
        speak(`Say ${level.word.toUpperCase()}!`);
      }
    }
  }, [isListening, stopListening, startListening, settings.voiceEnabled, level, speak]);

  const handleUpdateSettings = useCallback((newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  // =========== RENDER ===========

  // Browser support check
  if (!micSupported || !ttsSupported) {
    return (
      <div className="pip-game pip-game--error">
        <div className="error-content">
          <div className="error-icon">😢</div>
          <h2>Browser Not Supported</h2>
          <p>This game requires speech recognition and text-to-speech.</p>
          <p>Please use <strong>Chrome</strong>, <strong>Edge</strong>, or <strong>Safari</strong>.</p>
          <button className="btn btn--primary" onClick={onExit}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pip-game">
      {/* TITLE SCREEN */}
      {screen === 'title' && (
        <TitleScreen
          onStartNew={handleStartNew}
          onContinue={handleContinue}
          onSettings={() => { playClick(); setShowSettings(true); }}
          hasSavedProgress={!!savedProgress}
          savedLevel={savedProgress?.level}
        />
      )}

      {/* GAME SCREEN */}
      {screen === 'game' && level && (
        <GameScreen
          ref={gameScreenRef}
          level={level}
          levelNumber={currentLevel + 1}
          totalLevels={LEVELS.length}
          stars={stars}
          isFirstLevel={isGameStarted}  // Only true on first level
          isListening={isListening}
          isSpeaking={isSpeaking}
          transcript={interimTranscript || transcript}
          settings={settings}
          onReady={handleReady}
          onHome={handleHome}
          onToggleMic={handleToggleMic}
          onLevelComplete={advanceLevel}
          speakAsPip={speakAsPip}
          speakPraise={speakPraise}
          speak={speak}
          playSound={{ playSuccess, playPop, playMunch, playGulp, playFridge, playWhoosh }}
        />
      )}

      {/* COMPLETE SCREEN */}
      {screen === 'complete' && (
        <CompleteScreen
          stars={stars}
          totalLevels={LEVELS.length}
          onPlayAgain={handlePlayAgain}
          onHome={onExit}
        />
      )}

      {/* SETTINGS MODAL */}
      {showSettings && (
        <SettingsModal
          settings={settings}
          onUpdate={handleUpdateSettings}
          onClose={() => { playClick(); setShowSettings(false); }}
          onReset={() => {
            localStorage.removeItem('pip_progress');
            setSavedProgress(null);
          }}
        />
      )}

      {/* MIC ERROR TOAST */}
      {micError && (
        <div className="toast toast--error">{micError}</div>
      )}
    </div>
  );
}
